/* One booking on the schedule: time, sleeve, title, host, genres, state.
 *
 * Three variants, and each carries its state in at least two ways because
 * colour is never allowed to be the only signal here. Past is muted AND struck
 * through AND says "Aired". Live is an accent field AND 4px rules top and bottom
 * AND says ON AIR. Upcoming is the plain row and says "Up next" or
 * "Scheduled". Turn the screen greyscale and nothing is lost.
 *
 * The state is derived from `status` first and from the clock second. The
 * server owns `on_air` — it knows whether a DJ actually authenticated, which
 * a timestamp comparison cannot — and the clock is only there to age a
 * `scheduled` row out once its window has passed between polls.
 *
 * `is_repeat` gets the (R) suffix, in the body weight and not uppercase, so it
 * reads as an annotation on the title rather than part of it.
 */

import { Link } from 'react-router-dom';

import { api } from '../api';
import { formatTimeRange } from '../format';
import type { TzMode } from '../format';
import type { SlotCard, SlotKind } from '../types';
import { Artwork } from './Artwork';
import { Chip, ChipList } from './Chip';
import { OnAirBadge } from './OnAirBadge';
import { slotHostNames } from './shared';

export type SlotState = 'past' | 'live' | 'upcoming';

/** What the slot is, in words. The non-colour half of live vs recorded. */
const KIND_WORDS: Record<SlotKind, string> = {
  live: 'Live from the booth',
  prerecorded: 'Pre-recorded',
  repeat: 'Repeat',
  fallback: 'Archive shuffle',
};

export function slotState(slot: SlotCard, nowMs: number): SlotState {
  if (slot.status === 'on_air') return 'live';
  if (slot.status === 'aired' || slot.status === 'cancelled') return 'past';

  const start = Date.parse(slot.starts_at);
  const end = Date.parse(slot.ends_at);
  if (Number.isFinite(end) && end <= nowMs) return 'past';
  if (Number.isFinite(start) && Number.isFinite(end) && start <= nowMs && nowMs < end) {
    return 'live';
  }
  return 'upcoming';
}

export interface ScheduleRowProps {
  slot: SlotCard;
  tz: TzMode;
  /**
   * The station's idea of now, from /api/live's `server_time`. Without it the
   * local clock is used, which is close enough to age a row out.
   */
  now?: string | null;
  /** Overrides the derived state. The caller sometimes knows better. */
  state?: SlotState;
  /** This is the next thing on. Changes the marker from "Scheduled" to "Up next". */
  isNext?: boolean;
  /** Draw the .ics link. One VEVENT, straight off the API. */
  showIcs?: boolean;
  /** Routes a genre chip. Without it the chips are labels. */
  genreHref?: (slug: string) => string;
  /** `li` when the caller has wrapped these in a list. */
  as?: 'div' | 'li';
  className?: string;
}

export function ScheduleRow({
  slot,
  tz,
  now,
  state,
  isNext = false,
  showIcs = false,
  genreHref,
  as = 'div',
  className,
}: ScheduleRowProps) {
  const nowMs = now ? Date.parse(now) : Date.now();
  const resolved = state ?? slotState(slot, Number.isFinite(nowMs) ? nowMs : Date.now());
  const cancelled = slot.status === 'cancelled';
  const isFallback = slot.kind === 'fallback';

  const marker = cancelled
    ? 'Cancelled'
    : isFallback
      ? 'Fallback'
      : resolved === 'past'
        ? 'Aired'
        : resolved === 'upcoming' && isNext
          ? 'Up next'
          : resolved === 'upcoming'
            ? 'Scheduled'
            : null;

  const who = slotHostNames(slot.title, slot.hosts);
  const meta = [who ? `w/ ${who}` : null, KIND_WORDS[slot.kind], slot.catalogue_no]
    .filter(Boolean)
    .join(' · ');

  const classes = [
    'row',
    resolved === 'live' ? 'row--live' : null,
    resolved === 'past' ? 'row--past' : null,
    cancelled ? 'row--cancelled' : null,
    isFallback ? 'row--fallback' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const title = (
    <>
      {slot.title}
      {slot.is_repeat ? <span className="row__repeat"> (R)</span> : null}
    </>
  );

  const Element = as;

  return (
    <Element className={classes}>
      <div className="mono row__time">{formatTimeRange(slot.starts_at, slot.ends_at, tz)}</div>

      <Artwork
        color={slot.artwork_color}
        catalogueNo={slot.catalogue_no}
        display={isFallback ? '24/7' : undefined}
        size="md"
        className="row__art"
      />

      <div className="row__mid">
        <div className="row__title">
          {slot.episode_catalogue_no ? (
            <Link to={`/episodes/${encodeURIComponent(slot.episode_catalogue_no)}`}>{title}</Link>
          ) : (
            title
          )}
        </div>
        <div className="mono row__meta">{meta}</div>
        {slot.genres.length > 0 ? (
          <ChipList label="Genres" className="row__chips">
            {slot.genres.map((genre) => (
              <Chip
                key={genre.slug}
                color={genre.color_token}
                to={genreHref ? genreHref(genre.slug) : undefined}
              >
                {genre.name}
              </Chip>
            ))}
          </ChipList>
        ) : null}
      </div>

      {showIcs && !isFallback && resolved !== 'past' ? (
        <a
          className="mono row__ics"
          href={api.icsUrl(slot.catalogue_no)}
          aria-label={`Add ${slot.title} to a calendar`}
        >
          + Calendar
        </a>
      ) : null}

      <div className="row__state">
        {resolved === 'live' && !cancelled ? (
          <OnAirBadge live size="sm" />
        ) : (
          <span className="mono">{marker}</span>
        )}
      </div>
    </Element>
  );
}
